import { CollapsibleTile, CollapsibleTileSize } from '@box-types';
import { generateTagOptions, generateTitleSizeClass, getHostClasses } from '@box/utils';
import React, { forwardRef, JSX, memo, useMemo } from 'react';
import { PromoTagComponent } from '../promo-tag';
import cn from 'classnames';
import isEqual from 'lodash-es/isEqual';
import { motion } from '@motionone/react';
import { SmartMedia } from '@box/component-library';

export type TileType = 'cuisine' | 'brand';

type CollapsibleTileProps<T> = {
  collapsed: boolean;
  size: CollapsibleTileSize;
  tile: CollapsibleTile<T>;
  className?: string;
  type?: TileType;
  mediaType?: 'lottie' | 'image' | 'gif';
};

const MotionDiv: React.FC<React.HTMLAttributes<HTMLDivElement>> = motion.div;
const MotionImg = motion.img as React.ComponentType<
  React.ImgHTMLAttributes<HTMLImageElement> & { animate?: any; transition?: any }
>;

function CollapsibleTileComponentInner<T>(
  props: CollapsibleTileProps<T>,
  ref?: React.Ref<HTMLDivElement>
): JSX.Element | null {
  const { collapsed, size, tile, className, type = 'cuisine', mediaType = 'image' } = props;

  const expandedTileSizeClass = getHostClasses(size);
  const titleSizeClass = generateTitleSizeClass(tile?.title || '');
  const tagOptions = useMemo(() => generateTagOptions(tile), [tile]);
  const tileState = useMemo(() => {
    if (!tile) return null;

    const fallbackImage = '/assets/default-tile.png'; // ✅ your local fallback

    const rawImage = tile.image ?? fallbackImage;
    const rawCollapsedImage = tile.collapsedImage ?? rawImage;

    return {
      ...tile,
      backGroundColor: tile.backGroundColor ?? '#5a5a5a',
      textColor: tile.textColor ?? '#ffffff',
      image: rawImage,
      collapsedImage: rawCollapsedImage,
      multiplier: tile.multiplier ?? 1,
      points: tile.points ?? 1,
      action: tile.action ?? null
    };
  }, [tile]);

  if (!tileState) return null;

  // const cleanImageSrc = tileState.image.replace(/^url\(["']?(.+?)["']?\)$/, '$1');
  // const cleanCollapsedImageSrc = tileState.collapsedImage.replace(/^url\(["']?(.+?)["']?\)$/, '$1');

  const motionPropsCollapsed = {
    initial: { width: 0, opacity: 0 },
    animate: { width: '100%', opacity: 1 },
    exit: { width: 0, opacity: 0 },
    transition: { duration: 0.4, easing: 'ease-in-out' as const },
    style: { display: 'inline-block' }
  };

  const motionPropsCollapsedImage = {
    animate: { rotate: [0, 10, 0] },
    transition: {
      duration: 1,
      times: [0, 0.5, 1],
      easing: 'ease-in-out'
    }
  };

  const motionPropsExpanded = {
    initial: { width: 0, opacity: 0, scale: 0 },
    animate: { width: '100%', opacity: 1, scale: 1 },
    exit: { width: 0, opacity: 0 },
    transition: { duration: 0.2, easing: 'ease-in-out' as const },
    style: { display: 'inline-block' }
  };

  const rotationSequences = [
    [0, 10, 0],
    [0, -10, 0],
    [0, 15, 0],
    [0, 20, 0],
    [0, -20, 0]
  ];

  const selectedRotation = useMemo(() => {
    const index = Math.floor(Math.random() * rotationSequences.length);
    return rotationSequences[index];
  }, []);

  const motionPropsExpandedImage = {
    initial: { scale: 0.8 },
    animate: { rotate: selectedRotation, scale: 1 },
    transition: {
      duration: 1,
      times: Array(selectedRotation.length)
        .fill(0)
        .map((_, i) => i / (selectedRotation.length - 1)),
      ease: 'easeInOut'
    }
  };

  // Brand Layout
  if (type === 'brand') {
    return (
      <>
        {collapsed ? (
          <MotionDiv
            key="collapsed"
            {...motionPropsCollapsed}
            className={cn(className, 'collapsed-tile-component collapsed-tile-component-brand')}
            style={{ color: tileState.textColor, backgroundColor: tileState.backGroundColor }}
          >
            <span className="collapsible-tile-component-icon material-icons">expand_more</span>
            <div className="collapsed-tile-component-title ods-typography-subtitle-s">
              {tileState.collapsedTitle ?? tileState.title}
            </div>
            {tagOptions && (
              <PromoTagComponent
                className="box-promo-tag expanded-tile-component-tag"
                options={tagOptions}
                enableAnimations={false}
              />
            )}
            <MotionImg
              className="collapsed-tile-component-image-background"
              src={tileState.image || undefined}
              alt={tileState.title}
              onError={(e) => (e.currentTarget.src = undefined)}
              {...motionPropsCollapsedImage}
            />
          </MotionDiv>
        ) : (
          <div
            className={cn(className, 'expanded-tile-component expanded-tile-brand')}
            aria-disabled={tileState.disabled}
            style={{ color: tileState.textColor, backgroundColor: tileState.backGroundColor }}
          >
            {tagOptions && (
              <PromoTagComponent
                className="box-promo-tag expanded-tile-component-tag"
                options={tagOptions}
                enableAnimations={false}
              />
            )}
            <div className={`expanded-tile-component-title ods-typography-subtitle-s ${titleSizeClass}`}>
              {tileState.title}
            </div>
            <div
              className="expanded-tile-component-image-background-brand"
              style={{ backgroundImage: `url(${tileState.image})` }}
            />
          </div>
        )}
      </>
    );
  }

  // Cuisine Layout
  return collapsed ? (
    <MotionDiv key="collapsed" {...motionPropsCollapsed}>
      <div className={cn(className, 'collapsed-tile-component')}>
        <span className="collapsible-tile-component-icon material-icons">expand_more</span>
        <div className="collapsed-tile-component-title ods-typography-subtitle-s">{tileState.title}</div>
        {tagOptions && <div>todo points tag component</div>}
        {mediaType === 'lottie' ? (
          <SmartMedia
            src={tileState.collapsedImage}
            alt={tileState.title}
            type="lottie"
            className="collapsed-tile-component-image-background"
          />
        ) : (
          <MotionImg
            className="collapsed-tile-component-image-background"
            src={tileState.collapsedImage || undefined}
            alt={tileState.title}
            onError={(e) => (e.currentTarget.src = undefined)}
            {...motionPropsCollapsedImage}
          />
        )}
      </div>
    </MotionDiv>
  ) : (
    <MotionDiv key="expanded" {...motionPropsExpanded}>
      <div
        className={cn(className, 'expanded-tile-component', expandedTileSizeClass)}
        aria-disabled={tileState.disabled}
        ref={ref}
      >
        {tagOptions && (
          <PromoTagComponent
            className="box-promo-tag expanded-tile-component-tag"
            options={tagOptions}
            enableAnimations={false}
          />
        )}

        {mediaType === 'lottie' ? (
          <SmartMedia
            src={tileState.image}
            alt={tileState.title}
            type="lottie"
            className="expanded-tile-component-image-background"
          />
        ) : (
          <MotionImg
            className="expanded-tile-component-image-background"
            src={tileState.image || undefined}
            alt={tileState.title}
            onError={(e) => (e.currentTarget.src = undefined)}
            {...motionPropsExpandedImage}
          />
        )}

        <div className={`expanded-tile-component-title ods-typography-body-s-bold ${titleSizeClass}`}>
          {tileState.title}
        </div>
      </div>
    </MotionDiv>
  );
}

const CollapsibleTileComponent = forwardRef(CollapsibleTileComponentInner) as <T>(
  props: CollapsibleTileProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element | null;

const areEqual = (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
};

export default memo(CollapsibleTileComponent, areEqual);
